import MapMercureModel         from '@models/map/MapMercureModel';
import dayjs                   from 'dayjs';
import { EventSourcePolyfill } from 'event-source-polyfill';
import Proto                   from '@/proto/proto';
import { Buffer }              from 'buffer';
import _get                    from 'lodash/get';
import { getIdFromUrn }        from '@widesk-core/tools/identifier';
import currentEditorStore      from '@/TokamapEditor/stores/currentEditorStore';
import { DrawTriggerOptions }  from '@tokamap/core';
import { mercatorToLngLat }    from '@tokamap/core';
import { rawRotationToDeg }    from '@tokamap/core';
import { normalizeFraming }    from '@tokamap/core';
import { featureTypeByUrn }    from '@/TokamapEditor/helpers/featureTypes';
import PlaceModel              from '@models/map/PlaceModel';
import { formatProtobufTimestamp } from '@/TokamapEditor/stores/mercureModification';

export type MercureEvent = { data: string, type: string };
export type MercureEventData = {
	kind: 'feature.updated' | 'feature.created' | 'feature.deleted' | 'map.updated';
	data: string;
	proto: string;
};

const services = window.APP_CONFIG?.services || [];
const serviceMercure = services.find(service => service.id === 'mercure');

export default class EditorMercureListener {
	private _authTimeout?: ReturnType<typeof setTimeout>;
	private _eventSource?: EventSourcePolyfill;

	private _messageQueue: MercureEvent[];
	private _messageQueueIsPaused: boolean;

	private _token: MapMercureModel;

	private readonly _mapUrn: Urn;

	public constructor(mapUrn: Urn, messageQueueIsPaused = false) {
		this._mapUrn = mapUrn;
		this._messageQueue = [];
		this._messageQueueIsPaused = messageQueueIsPaused;
		this._token = new MapMercureModel();
	}

	public pauseMessageQueue(): this {
		this._messageQueueIsPaused = true;
		return this;
	}

	public resumeMessageQueue() {
		this._messageQueue.forEach(e => this.executeMessageFunction(e));
		this._messageQueueIsPaused = false;
		this._messageQueue = [];
		return this;
	}

	public async start() {
		const topics = [this._mapUrn];

		this.stop();

		await this._fetchToken();

		const url = new URL(serviceMercure?.configurations.api_endpoint || '');

		if (topics.length) {
			topics.forEach(topic => url.searchParams.append('topic', topic));
		}

		this._eventSource = new EventSourcePolyfill(url.toString().replace('/?', '?'), {
			headers: { 'Authorization': `Bearer ${this._token.jwt}` },
		});

		this._eventSource.onmessage = this._onMessage;
	}

	public stop(): void {
		if (this._eventSource) {
			this._eventSource.close();
		}

		if (this._authTimeout) {
			clearTimeout(this._authTimeout);
		}
	}

	private async _fetchToken() {
		const mapId = getIdFromUrn(this._mapUrn);
		await this._token.setId(mapId).fetch();
		const timeRemaining = this._token.exp.diff(dayjs(), 'second');
		this._authTimeout = setTimeout(this._fetchToken.bind(this), timeRemaining * 1000);
	}

	private _onMessage = async (e: MercureEvent) => {
		if (this._messageQueueIsPaused) {
			this._messageQueue.push(e);
		} else {
			return setTimeout(() => this.executeMessageFunction(e));
		}
	};

	private async executeMessageFunction(e: MercureEvent) {
		const eventData = JSON.parse(e.data) as MercureEventData;
		const EventProto = _get(Proto, eventData.proto);

		if (EventProto) {
			const proto = EventProto.decode(Buffer.from(eventData.data, 'base64'));

			console.log(eventData.kind);

			const options: DrawTriggerOptions = { skipConfirm: true, skipHistory: true, skipSave: true };

			switch (eventData.kind) {
				case 'feature.deleted': {
					const shape = currentEditorStore.findShapeFromFeatureId(proto.id);
					if (shape) return currentEditorStore.get.deleteShape(shape.id, options);
					break;
				}
				case 'feature.created':
				case 'feature.updated': {
					const currentShape = currentEditorStore.findShapeFromFeatureId(proto.id);

					// Écho de nos propres enregistrements, peut-être plus ancien que l'état local (voir
					// `isLocallySaving`).
					if (currentShape && currentEditorStore.isLocallySaving(currentShape.id)) break;

					const currentEntityId = (currentShape?.entity as any)?.entity?.id;
					const newEntityId = proto.entityUrn ? getIdFromUrn(proto.entityUrn) : undefined;

					// On ne refetch le Place que si l'entité a réellement changé (évite une requête inutile à chaque écho Mercure de nos propres modifications).
					const entity = !proto.entityUrn
						? undefined
						: String(currentEntityId) === String(newEntityId)
							? currentShape?.entity
							: await fetchEntityFromUrn(proto.entityUrn);

					return currentEditorStore.get.addShapeFromFeature({
						type: 'Feature',
						geometry: {
							type: featureTypeByUrn[formatUrn(proto.featureType)!]!.geoJsonType as any,
							coordinates: Object.values(parseStruct(proto.coordinates)) as any,
							properties: {
								id: currentEditorStore.getShapeIdFromFeatureId(proto.id),
								extra: parseStruct(proto.extra),
								entity,
								// Le thème de la forme LOCALE : `Feature.proto` n'a pas de champ `theme`, la forme
								// reconstruite reviendrait donc sans thème et écraserait celui en place. Un thème
								// posé ailleurs n'arrive pas en direct tant que le proto ne le porte pas.
								theme: currentShape?.themeId,
							},
						},
					}, {
						layer: Number(proto.layer),
						...options,
					});
				}

				case 'map.updated': {
					const protoMap = proto as Proto.Platform.Service.Map.Map;
					const configuration = parseStruct(protoMap.configuration);
					const map = currentEditorStore.map;

					if (!map) return;

					if (
						!map.attributes.updatedAt
						|| map.updatedAt.unix() < dayjs(formatProtobufTimestamp(proto.updatedAt)).unix()
					) {
						const framing = normalizeFraming(configuration.framing);

						if (framing) {
							currentEditorStore.get.setFraming(framing, options);
						} else {
							currentEditorStore.get.setMapView({
								center: mercatorToLngLat(configuration.center),
								rotationDeg: rawRotationToDeg(configuration.rotation),
								zoom: configuration.zoom,
								zoomMax: configuration.zoomMax,
								zoomMin: configuration.zoomMin,
							}, options);
						}

						currentEditorStore.get.setGeolocationEnabled(configuration.geolocation, options);

						currentEditorStore.get.setTileLayerName(configuration.tileLayerName, options);

						// `setOsmOptions` fusionne là où une synchronisation voudrait remplacer — sans
						// conséquence, l'objet enregistré portant toujours une valeur par réglage.
						if (configuration.osmOptions) {
							currentEditorStore.get.setOsmOptions(configuration.osmOptions, options);
						}

						if (configuration.renderingOptions) {
							currentEditorStore.get.setRenderingOptions(configuration.renderingOptions, options);
						}

						currentEditorStore.get.setBackgroundColor(configuration.backgroundColor, options);

						currentEditorStore.readMapKindFrom(configuration);

						return;
					}
					break;
				}
			}
		}
	}
}

async function fetchEntityFromUrn(entityUrn: string): Promise<any> {
	const place = new PlaceModel({ id: getIdFromUrn(entityUrn as Urn) });
	await place.fetch();

	// Image lue dans le cache, comme à la liaison (`linkPlaceToShape`) : un rechargement forcé de la
	// liste des fichiers pouvait revenir sans l'image du point d'intérêt, et la forme la perdait.
	return fetchEntityFromPlace(place, false);
}

export async function fetchEntityFromPlace(place: PlaceModel, forceImage = true): Promise<any> {
	return {
		type: 'Place',
		entity: {
			id: Number(place.id),
			label: place.label,
			location: place.location,
			image: await place.fetchImageSrc(forceImage),
			// 'fields': { // TODO récupérer les fields avec une requete
			// 	'$:map:field:52': 'HALL 3', // TODO
			// 	'$:map:field:54': { // TODO
			// 		'en': 'Yuzu Stage', // TODO
			// 		'fr': 'Scène Yuzu', // TODO
			// 	}, // TODO
			// 	'$:map:field:53': null, // TODO
			// },
		},
	};
}

function parseStruct(struct: any): any {
	const result: Record<string, any> = {};

	for (const key in struct.fields) {
		const value = struct.fields[key];

		if (Object.prototype.hasOwnProperty.call(value, 'stringValue')) {
			result[key] = value.stringValue;
		} else if (Object.prototype.hasOwnProperty.call(value, 'boolValue')) {
			result[key] = value.boolValue;
		} else if (Object.prototype.hasOwnProperty.call(value, 'numberValue')) {
			result[key] = value.numberValue;
		} else if (Object.prototype.hasOwnProperty.call(value, 'nullValue')) {
			result[key] = null;
		} else if (Object.prototype.hasOwnProperty.call(value, 'structValue')) {
			result[key] = parseStruct(value.structValue);
		} else if (Object.prototype.hasOwnProperty.call(value, 'listValue')) {
			result[key] = value.listValue.values.map(parseValue);
		} else {
			result[key] = null;
		}
	}

	return result;
}

function parseValue(value: any): any {
	if (Object.prototype.hasOwnProperty.call(value, 'stringValue')) return value.stringValue;
	if (Object.prototype.hasOwnProperty.call(value, 'boolValue')) return value.boolValue;
	if (Object.prototype.hasOwnProperty.call(value, 'numberValue')) return value.numberValue;
	if (Object.prototype.hasOwnProperty.call(value, 'nullValue')) return null;
	if (Object.prototype.hasOwnProperty.call(value, 'structValue')) return parseStruct(value.structValue);
	if (Object.prototype.hasOwnProperty.call(value, 'listValue')) return value.listValue.values.map(parseValue);
	return null;
}

function formatUrn(urn: { partition: string; service: string; resource: string; identifier: string; }): string {
	return `${urn.partition}:${urn.service}:${urn.resource}:${urn.identifier}`;
}